import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { inr } from "@/lib/format";
import { changePayment } from "@/lib/orderHistoryApi";

const paise = value => Math.round(Number(value || 0) * 100);

/**
 * Correct a settled bill's payment type without reopening the table (owner,
 * 2026-10-24: a bill settled as cash that was paid by UPI). Only the tenders
 * change; they must add up to the bill, as at settlement. The server decides who
 * may and until when, and says so by name when it refuses.
 */
export default function ChangePaymentDialog({ bill, modes, token, onClose, onChanged }) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!bill) return;
    const current = String(bill.payment_method || "").toLowerCase();
    const other = modes.find(m => String(m.name).toLowerCase() !== current) || modes[0];
    setRows([{ mode: other?.name || "", amount: String(Number(bill.total).toFixed(2)) }]);
  }, [bill, modes]);

  if (!bill) return null;

  const total = paise(bill.total);
  const entered = rows.reduce((sum, r) => sum + paise(r.amount), 0);
  const left = total - entered;
  const ready = rows.length > 0 && left === 0 && rows.every(r => r.mode && paise(r.amount) > 0);

  const set = (i, patch) => setRows(rs => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const addSplit = () => setRows(rs => [...rs, { mode: modes[0]?.name || "", amount: String(Math.max(left, 0) / 100) }]);
  const remove = i => setRows(rs => rs.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      const result = await changePayment(
        bill.id, bill.version,
        rows.map(r => ({ mode: r.mode, amount: paise(r.amount) / 100 })),
        token,
      );
      toast.success(result?.changed === false ? "The payment was already recorded that way" : `Bill #${bill.bill_number ?? bill.order_number} payment changed`);
      onChanged?.();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || err?.message || "The payment was not changed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={open => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="change-payment-dialog">
        <DialogHeader>
          <DialogTitle>Change payment · Bill #{bill.bill_number ?? bill.order_number}</DialogTitle>
          <DialogDescription>
            Bill total {inr(bill.total)}. Only how it was paid changes; the bill stays as it is.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-tight">
          {rows.map((row, i) => (
            <div key={i} className="flex items-center gap-tight">
              <select
                value={row.mode}
                onChange={e => set(i, { mode: e.target.value })}
                className="h-9 flex-1 rounded border bg-background px-2 text-body"
                aria-label="Payment type"
                data-testid={`change-payment-mode-${i}`}
              >
                {modes.map(m => <option key={m.id ?? m.name} value={m.name}>{m.display_label || m.name}</option>)}
              </select>
              <Input
                type="number" step="0.01" min="0" inputMode="decimal"
                value={row.amount}
                onChange={e => set(i, { amount: e.target.value })}
                className="h-9 w-32 text-right"
                aria-label="Amount"
                data-testid={`change-payment-amount-${i}`}
              />
              {rows.length > 1 && (
                <button type="button" onClick={() => remove(i)} aria-label="Remove this payment" className="h-9 w-9 inline-flex items-center justify-center text-muted-foreground hover:text-foreground">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          <div className="flex items-center justify-between">
            <Button type="button" variant="ghost" size="sm" onClick={addSplit} data-testid="change-payment-split">
              <Plus className="h-3.5 w-3.5 mr-tight" /> Split
            </Button>
            <span className={`text-meta ${left === 0 ? "text-muted-foreground" : "text-danger"}`} data-testid="change-payment-left">
              {left === 0 ? "Adds up to the bill" : left > 0 ? `${inr(left / 100)} still to place` : `${inr(-left / 100)} more than the bill`}
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Keep it</Button>
          <Button variant="action" type="button" onClick={save} disabled={!ready || saving} data-testid="change-payment-save">
            {saving ? "Saving…" : "Change payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
